/**
 * Sanitize a backend pipeline/diagnosis failure message for display.
 *
 * The BE's `pipeline_result.message` is sometimes the *downstream* error rather
 * than the real failure reason — e.g. a failed repair records
 * `"geometry <uuid> has no s3_key"` (the missing-artifact error), which means
 * nothing to a user. Other messages are real prose but leak raw snake_case
 * identifiers (`self_intersection`, `non_manifold_edges`) straight from the
 * detector. This helper:
 *   1. strips the machine suffix (`" -- targeted: …; remaining: …"`),
 *   2. drops messages that are pure internal plumbing (returns null so the
 *      caller falls back to its own friendly generic copy),
 *   3. humanizes stray snake_case tokens in the messages that ARE shown.
 *
 * It can only decide whether a message is fit to show and tidy it — it can't
 * invent a better reason. The real fix is BE-side (record the actual repair
 * failure reason); this is the interim safety net.
 *
 * The separate `translateFailureMessage` pass below matches the *cleaned*
 * English string against known BE-emitted phrase patterns (STL-diagnosis
 * variants — non-manifold edges, self-intersection, "surfaces could not be
 * healed", …) and swaps them for a locale-aware string. Anything unmatched
 * falls through as the cleaned English — better than dropping to a generic
 * fallback that loses the specific reason.
 */
import type { ParseKeys, TFunction } from 'i18next';

// Messages that are internal plumbing, not a user-facing reason — show the
// caller's generic fallback instead. Matches the whole message being a bare id,
// or the message containing an artifact/exception marker.
const UNUSABLE =
  /s3[_ ]?key|traceback|stack trace|nonetype|keyerror|errno|^\s*[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f-]+\s*$|^\s*(null|undefined)\s*$/i;

/** Replace snake_case identifiers with spaced words, keeping the canonical
 *  hyphenated form for the compound defect terms we use elsewhere. */
function humanizeTokens(s: string): string {
  return s
    .replace(/[a-z0-9]+(?:_[a-z0-9]+)+/gi, (m) => m.replace(/_/g, ' '))
    .replace(/\bself intersection/gi, 'self-intersection')
    .replace(/\bnon manifold/gi, 'non-manifold');
}

/**
 * Clean a BE failure message for display, or null when it carries nothing
 * user-facing (caller should then show its own generic copy).
 */
export function cleanFailureMessage(
  message: string | null | undefined,
): string | null {
  if (!message) return null;
  const head = message.split(' -- ')[0].trim();
  if (!head || UNUSABLE.test(head)) return null;
  return humanizeTokens(head);
}

// Ordered pattern → i18n-key table. First-match wins, so more specific
// phrases come before broader token matches. Every match runs on the
// LOWERCASED cleaned message; the sentence-case original is available to
// the caller as fallback.
//
// Sources of the patterns:
//   - `failureMessage.test.ts` covers "surfaces could not be healed",
//     "could not resolve self-intersection", "found non-manifold edges".
//   - BE STL-diagnosis paths emit variants around watertightness,
//     boundary edges, degenerate triangles, and topology repair timeouts.
//   - "Repair timed out" (test cases + observed pipeline_result rows).
//
// New: STL-specific messages the user surfaced ("STL isn't watertight",
// bad tri counts, unreadable STL header) — routed here so the finding
// rail shows a localized reason instead of the raw BE string.
const KNOWN_FAILURE_PATTERNS: readonly [RegExp, ParseKeys][] = [
  // STL-specific
  [
    /stl.*not\s+watertight|stl.*open\s+edges|open\s+shell/i,
    'compMesh.failureMessage.stlNotWatertight',
  ],
  [
    /degenerate\s+(triangle|face)/i,
    'compMesh.failureMessage.degenerateTriangles',
  ],
  [
    /stl\s+header|(?:invalid|malformed|corrupt)\s+stl/i,
    'compMesh.failureMessage.stlUnreadable',
  ],
  [
    /no\s+triangles|empty\s+stl|zero\s+triangles/i,
    'compMesh.failureMessage.stlEmpty',
  ],
  [
    /single\s+solid|only\s+one\s+solid|no\s+named\s+solid/i,
    'compMesh.failureMessage.stlSingleSolid',
  ],
  // General topology defects
  [/self-intersection/i, 'compMesh.failureMessage.selfIntersection'],
  [/non-manifold/i, 'compMesh.failureMessage.nonManifold'],
  [/pinch\s+point/i, 'compMesh.failureMessage.pinchPoint'],
  [/overlap|coincident\s+face/i, 'compMesh.failureMessage.overlappingFaces'],
  // Repair pipeline outcomes
  [
    /could\s+not\s+be\s+healed|cannot\s+be\s+healed|healing\s+failed/i,
    'compMesh.failureMessage.couldNotHeal',
  ],
  [/timed?\s+out|timeout/i, 'compMesh.failureMessage.timedOut'],
  [/out\s+of\s+memory|oom\b/i, 'compMesh.failureMessage.outOfMemory'],
  // Format / IO
  [
    /unsupported\s+(format|file)|format\s+not\s+supported/i,
    'compMesh.failureMessage.unsupportedFormat',
  ],
  [
    /could\s+not\s+be\s+read|failed\s+to\s+read|cannot\s+read/i,
    'compMesh.failureMessage.couldNotRead',
  ],
  // Volume / geometry
  [/zero\s+volume|no\s+solid\s+volume/i, 'compMesh.failureMessage.zeroVolume'],
  [/no\s+geometry|empty\s+file/i, 'compMesh.failureMessage.noGeometry'],
];

/**
 * Localize a cleaned BE failure message by matching it against a set of
 * known phrase patterns. Returns the localized string when a pattern
 * matches, or null when nothing matches (caller falls back to the raw
 * cleaned English — better than a generic string that drops the reason).
 *
 * `cleaned` must be the output of `cleanFailureMessage(...)` — the
 * patterns are calibrated against hyphenated compound tokens
 * (`self-intersection`, `non-manifold`) which `humanizeTokens` produces.
 */
export function translateFailureMessage(
  cleaned: string | null | undefined,
  t: TFunction,
): string | null {
  if (!cleaned) return null;
  for (const [pattern, key] of KNOWN_FAILURE_PATTERNS) {
    if (pattern.test(cleaned)) return t(key) as string;
  }
  return null;
}
